import { useState } from 'react';
import { CalendarDays, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Input } from '@components/ui/input';
import { quote, money, upcomingMonths, type AdSize, type PubChoice } from '@generated/components/pricing';

interface Props { sizes: AdSize[]; onApply: (p: { pub: PubChoice; sizeId: string; start: string; summary: string; businessName: string }) => void }

const KINDS = ['Grand opening', 'Sale or special offer', 'Concert / performance', 'Art show / exhibit', 'Fundraiser / benefit', 'Holiday event', 'Class or workshop', 'Other'];
type Aud = 'brooklyn' | 'manhattan' | 'general';
const AUDIENCES: { id: Aud; label: string; hint: string }[] = [
  { id: 'brooklyn', label: 'Brooklyn', hint: 'Red Hook, Carroll Gardens & South Brooklyn' },
  { id: 'manhattan', label: 'Manhattan', hint: 'East Village, Greenwich Village & nearby' },
  { id: 'general', label: 'General audience', hint: 'Worth traveling to, or not tied to a place: a movie, TV show, game, or something online' },
];
// Brooklyn only → Red Hook; Manhattan only → Village; anything wider → both papers.
function pubFor(a: Aud[]): PubChoice | null {
  if (a.length === 0) return null;
  if (a.length === 1 && a[0] === 'brooklyn') return 'red-hook';
  if (a.length === 1 && a[0] === 'manhattan') return 'village';
  return 'bundle';
}
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Which issue should carry an event ad: the event's own month, or the month before if it's in the first week. */
function pickIssue(dateStr: string) {
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const issueDate = d.getDate() <= 7 ? new Date(d.getFullYear(), d.getMonth() - 1, 1) : new Date(d.getFullYear(), d.getMonth(), 1);
  const label = `${MONTHS[issueDate.getMonth()]} ${issueDate.getFullYear()}`;
  const up = upcomingMonths();
  const hit = up.find((m) => m.label === label);
  return { label, deadline: hit?.deadline ?? '', available: !!hit, early: d.getDate() <= 7 };
}

export function EventPlanner({ sizes, onApply }: Props) {
  const [biz, setBiz] = useState('');
  const [eventName, setEventName] = useState('');
  const [kind, setKind] = useState('');
  const [date, setDate] = useState('');
  const [aud, setAud] = useState<Aud[]>([]);
  const area = pubFor(aud);
  const toggleAud = (id: Aud) => setAud((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const [budget, setBudget] = useState('');
  const [show, setShow] = useState(false);
  const amount = Number(budget.replace(/[^0-9.]/g, '')) || 0;
  const issue = date ? pickIssue(date) : null;
  const ready = !!biz.trim() && !!eventName.trim() && !!date && !!area && amount > 0;

  const picks = area ? sizes.map((s) => ({ s, qt: quote(s, area, 1, false) })).filter((o) => o.qt.total <= amount)
    .sort((a, b) => b.qt.total - a.qt.total).slice(0, 3) : [];
  const cheapest = area && sizes.length ? Math.min(...sizes.map((s) => quote(s, area, 1, false).total)) : 0;
  const when = date ? new Date(`${date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const summary = `Event promotion — Business: ${biz.trim()}; Event: ${eventName.trim()}${kind ? ` (${kind})` : ''}; Date: ${when}; Audience: ${aud.map((x) => AUDIENCES.find((a) => a.id === x)?.label ?? x).join(', ')}; Budget: ${money(amount)}; Issue: ${issue?.label ?? ''}`;

  if (show && issue && area) {
    return (
      <section className="border-y-4 border-double border-foreground bg-card p-5">
        <button onClick={() => setShow(false)} className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm underline underline-offset-2"><ArrowLeft className="h-4 w-4" />Change answers</button>
        <h3 className="font-[family-name:var(--font-heading)] text-2xl font-semibold">Promote {eventName.trim()} in the {issue.label} issue</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Your event is on {when}. {issue.early ? 'Because it falls in the first week of the month, the previous month\'s issue gives readers time to plan.' : 'That issue is on the street in the weeks leading up to it.'}
          {issue.deadline && ` Artwork is due ${issue.deadline}.`} Every print ad also runs on star-revue.com and in our email newsletter.
        </p>
        {!issue.available && (
          <p className="mt-3 flex items-start gap-2 border border-destructive p-3 text-sm text-destructive"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />The {issue.label} issue has already closed, or is too far off to book online yet. Call George at 917-652-9128 and we'll find the best option.</p>
        )}
        {issue.available && picks.length === 0 && <p className="mt-4 text-sm">The smallest ad in this paper is {money(cheapest)}. Raise your budget a little, or call 917-652-9128.</p>}
        {issue.available && (
          <div className="mt-4 grid gap-px border border-foreground bg-foreground sm:grid-cols-3">
            {picks.map(({ s, qt }, i) => (
              <div key={s.id} className="flex flex-col bg-background p-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">{i === 0 ? 'Most impact' : i === 1 ? 'Good balance' : 'Budget-friendly'}</div>
                <div className="mt-1 font-[family-name:var(--font-heading)] text-xl font-semibold">{s.name}</div>
                <div className="text-sm text-muted-foreground">One issue · {issue.label}</div>
                <div className="mt-2 font-[family-name:var(--font-heading)] text-2xl">{money(qt.total)}</div>
                <button onClick={() => onApply({ pub: area, sizeId: s.id, start: issue.label, summary, businessName: biz.trim() })} className="mt-auto min-h-11 bg-primary px-4 text-sm font-semibold text-primary-foreground">Choose this ad</button>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  const label = 'text-xs font-semibold uppercase tracking-wider';
  return (
    <section className="border-y-4 border-double border-foreground bg-card p-5">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary"><CalendarDays className="h-4 w-4" />Event Promotion</div>
      <h3 className="mt-2 font-[family-name:var(--font-heading)] text-2xl font-semibold">Tell us about your event</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label htmlFor="ev-biz" className={label}>Your business or organization<Input id="ev-biz" className="mt-1 h-11 normal-case tracking-normal" value={biz} onChange={(e) => setBiz(e.target.value)} /></label>
        <label htmlFor="ev-name" className={label}>Event name<Input id="ev-name" className="mt-1 h-11 normal-case tracking-normal" value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Holiday Open House" /></label>
        <label htmlFor="ev-date" className={label}>Event date<Input id="ev-date" type="date" className="mt-1 h-11 normal-case tracking-normal" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label htmlFor="ev-budget" className={label}>Budget for this event
          <span className="relative mt-1 block">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold">$</span>
            <Input id="ev-budget" inputMode="decimal" className="h-11 pl-7 normal-case tracking-normal" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="300" />
          </span>
        </label>
      </div>
      <fieldset className="mt-4"><legend className={label}>What kind of event?</legend>
        <div className="mt-2 flex flex-wrap gap-2">{KINDS.map((k) => <button key={k} type="button" onClick={() => setKind(k)} className={`min-h-11 border px-3 text-sm ${kind === k ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background hover:border-foreground/50'}`}>{k}</button>)}</div>
      </fieldset>
      <fieldset className="mt-4"><legend className={label}>Who is your audience? <span className="normal-case tracking-normal text-muted-foreground">(pick all that apply)</span></legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          {AUDIENCES.map((a) => {
            const on = aud.includes(a.id);
            return (
              <button key={a.id} type="button" aria-pressed={on} onClick={() => toggleAud(a.id)}
                className={`min-h-11 border p-3 text-left ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background hover:border-foreground/50'}`}>
                <div className="text-sm font-semibold">{on ? '✓ ' : ''}{a.label}</div>
                <div className={`text-xs ${on ? 'opacity-80' : 'text-muted-foreground'}`}>{a.hint}</div>
              </button>
            );
          })}
        </div>
        {area === 'bundle' && <p className="mt-2 text-xs text-muted-foreground">We'll recommend both papers: 10,000 combined circulation, plus our website and newsletter.</p>}
      </fieldset>
      <div className="mt-6 flex items-center gap-3">
        <button disabled={!ready} onClick={() => setShow(true)} className="min-h-11 bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-50">Show my recommendation</button>
        {!ready && <span className="text-xs text-muted-foreground">Fill in business, event, date, audience and budget.</span>}
      </div>
    </section>
  );
}
